import classNames from "classnames"
import { HTMLAttributes, useRef, useState } from "react"
import * as Icons from "@nova/component-icons"
import { useDropBox, UseDropBoxProps } from "./useDropBox"
import { Spinner, ThemeSizes } from "themes/default-tailwind"
import { DroppedFile, fileAllowableCheck, traverseFileTree } from "@nova/component-upload-dropbox"
import { mapExtensionsToMimeType } from "features/job-management/uploader/constants"

export const AddMoreDropBox = ({
    canAcceptMultiple = false,
    allowedFileExtensions,
    onNewItems,
    isProcessing,
    supportedMimeTypes,
    ...rest
}: {
    supportedMimeTypes: string[]
} & UseDropBoxProps &
    HTMLAttributes<HTMLDivElement>) => {
    const [isStaging, setIsStaging] = useState(false)
    const uploadFolderRef = useRef<HTMLInputElement>(null)

    //we need to run the file adding after a timeout so the component has a chance to re-render before the main thread gets blocked
    const handleStagingStart = async (callback: () => Promise<void>) => {
        setIsStaging(true)
        setTimeout(async () => {
            await callback()
            setIsStaging(false)
        }, 0)
    }

    const {
        uploadRef,
        hasUploadError,
        canDrop,
        setCanDrop,
        processingText,
        handleInputChange,
        formattedFileTypes,
        isOver,
        dropRef,
    } = useDropBox({
        canAcceptMultiple,
        allowedFileExtensions,
        onNewItems: (files) => handleStagingStart(() => onNewItems(files)),
        isProcessing,
    })
    const sortAndFilterFiles = (files: File[]) => {
        const filteredFiles = fileAllowableCheck(
            files,
            allowedFileExtensions,
            mapExtensionsToMimeType
        ) as DroppedFile[]

        const fileIsAllowed = filteredFiles.length !== 0
        if (!fileIsAllowed) return

        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })
        return filteredFiles.sort((a, b) =>
            collator.compare(a.fullPath || a.name, b.fullPath || b.name)
        )
    }

    const handleNewFolder = async (event: React.DragEvent) => {
        event.preventDefault()
        const items = event.dataTransfer.items
        let allFiles: DroppedFile[] = new Array(items.length)

        const isFolder = Array.from(items).some((item) => {
            const entry = item.webkitGetAsEntry?.()
            return entry && entry.isDirectory
        })
        if (!isFolder) return
        event.stopPropagation()
        for (let i = 0; i < items.length; i++) {
            const entry = items[i].webkitGetAsEntry?.()
            if (entry) {
                const files = await traverseFileTree(entry)
                allFiles = allFiles.concat(files)
            }
        }

        const filteredFiles = sortAndFilterFiles(allFiles)
        if (!filteredFiles) {
            return
        }

        onNewItems(filteredFiles)
    }

    const dropboxRef = useRef<HTMLDivElement>(null)
    dropRef(dropboxRef)

    return (
        <div className={classNames("select-none", rest.className)}>
            <div
                ref={dropboxRef}
                className={classNames(
                    "flex gap-2 items-center justify-center px-3 py-2 border rounded-md",
                    {
                        "border-dashed": !hasUploadError,
                        "border-red-500": hasUploadError || (isOver && !canDrop),
                        "border-white-300": !hasUploadError && !isOver,
                        "border-blue-500": isOver && canDrop,
                    }
                )}
                onDragEnter={(e) => {
                    e.preventDefault()
                    const items = e.dataTransfer?.items

                    let canDrop = true

                    for (let i = 0; i < items.length && canDrop; i++) {
                        const { type } = items[i]

                        if (
                            items[i].kind !== "file" ||
                            (!supportedMimeTypes.includes(type) && type !== "")
                        )
                            canDrop = false
                    }

                    setCanDrop(canDrop)
                }}
                onDrop={(e) => handleNewFolder(e)}
            >
                <Icons.Icon Icon={Icons.Backup} className="text-gray-0" />
                {!isProcessing && !isStaging ? (
                    <>
                        {hasUploadError && (
                            <h5
                                className="text-red-500 cursor-pointer"
                                onClick={() => uploadRef.current?.click()}
                            >
                                Incorrect File Type
                            </h5>
                        )}
                        {!hasUploadError && (
                            <h5 className="text-gray-0 normalLineHeight">
                                <span>
                                    Add More{" "}
                                    <span
                                        className="text-blue-200 cursor-pointer hover:text-blue-500 underline"
                                        onClick={() => uploadRef.current?.click()}
                                    >
                                        Files
                                    </span>{" "}
                                    or{" "}
                                    <span
                                        className="text-blue-200 cursor-pointer hover:text-blue-500 underline"
                                        onClick={() => uploadFolderRef.current?.click()}
                                    >
                                        Folders
                                    </span>
                                </span>
                            </h5>
                        )}
                    </>
                ) : (
                    <div className="flex gap-2.5 items-center">
                        <Spinner size={ThemeSizes.Medium} className="!h-auto" />
                        <h5 className="text-gray-0">{isStaging ? "Loading..." : processingText}</h5>
                    </div>
                )}

                <input
                    ref={uploadRef}
                    type="file"
                    multiple={canAcceptMultiple}
                    accept={formattedFileTypes}
                    onChange={handleInputChange}
                    className="hidden"
                />
                <input
                    ref={uploadFolderRef}
                    type="file"
                    multiple={canAcceptMultiple}
                    onChange={handleInputChange}
                    className="hidden"
                    // @ts-expect-error this is the supported way to allow folders
                    webkitdirectory="true"
                />
            </div>
        </div>
    )
}
